import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../hooks/LanguageContext';
import { spotlight, onSpotlightMove } from '../fx';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(22px, 5vw, 34px);
  }

  .archive-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .projects-filter {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
    margin-bottom: 8px;

    button {
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      padding: 8px 14px;
      border-radius: 999px;
      border: 1px solid var(--outline-light);
      background: transparent;
      color: var(--light-text-color);
      cursor: pointer;
      transition: var(--transition);

      &:hover {
        color: var(--primary-color);
        border-color: var(--primary-color);
      }

      &.active {
        background: var(--signal-color);
        border-color: var(--signal-color);
        color: var(--on-signal);
      }
    }
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 15px;
    position: relative;
    margin-top: 50px;
    scroll-margin-top: 200px;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.button};
    margin: 80px auto 0;
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .project-inner {
        transform: translateY(-7px);
      }
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .project-inner {
    ${spotlight};
    border: 1px solid var(--outline-light);
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--light-bg-color);
    transition: var(--transition);
  }

  .project-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: 35px;

    .folder {
      color: var(--primary-color);
      svg {
        width: 40px;
        height: 40px;
      }
    }

    .project-links {
      display: flex;
      align-items: center;
      margin-right: -10px;
      color: var(--light-text-color);

      a {
        ${({ theme }) => theme.mixins.flexCenter};
        padding: 5px 7px;

        &.external {
          svg {
            width: 22px;
            height: 22px;
            margin-top: -4px;
          }
        }

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .project-title {
    margin: 0 0 10px;
    color: var(--lightest-text-color);
    font-size: var(--fz-xxl);

    a {
      position: static;

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  .project-description {
    color: var(--light-text-color);
    font-size: var(--fz-lg);

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }
`;
const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              desc
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const gridRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const { t } = useLanguage();

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.filter(({ node }) => node);

  const techList = React.useMemo(() => {
    const allTech = projects.flatMap(({ node }) => node.frontmatter.tech || []);
    return [...new Set(allTech)].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [projects]);

  const filteredProjects = React.useMemo(() => {
    if (!activeFilter) {
      return projects;
    }
    return projects.filter(
      ({ node }) => node.frontmatter.tech && node.frontmatter.tech.includes(activeFilter),
    );
  }, [projects, activeFilter]);

  const handleFilterChange = tech => {
    const next = activeFilter === tech ? null : tech;
    if (next !== activeFilter) {
      setActiveFilter(next);
      setShowMore(false);
    }
  };

  // Collapsing removes most of the grid, so bring the section back into view
  const toggleShowMore = () => {
    if (showMore && gridRef.current) {
      gridRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }
    setShowMore(prev => !prev);
  };

  const firstBatch = filteredProjects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? filteredProjects : firstBatch;
  const hasMore = filteredProjects.length > GRID_LIMIT;

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion, activeFilter, projectsToShow.length]);

  const projectInner = node => {
    const { frontmatter } = node;
    const { github, external, title, tech, desc } = frontmatter;

    return (
      <div className="project-inner" onMouseMove={onSpotlightMove}>
        <header>
          <div className="project-top">
            <div className="folder">
              <Icon name="Folder" />
            </div>
            <div className="project-links">
              {github && (
                <a href={github} aria-label="GitHub Link" target="_blank" rel="noreferrer">
                  <Icon name="GitHub" />
                </a>
              )}
              {external && (
                <a
                  href={external}
                  aria-label="External Link"
                  className="external"
                  target="_blank"
                  rel="noreferrer">
                  <Icon name="External" />
                </a>
              )}
            </div>
          </div>

          <h3 className="project-title">
            <a href={external} target="_blank" rel="noreferrer">
              {title}
            </a>
          </h3>
          <div className="project-description">
            <p>{t(`projects_${desc}`)}</p>
          </div>
        </header>

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map((tech, i) => (
                <li key={i}>{tech}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection>
      <h2 ref={revealTitle}>{t('archive_text_title')}</h2>

      <Link className="inline-link archive-link" to="/archive" ref={revealArchiveLink}>
        {t('archive_text_subtitle')}
      </Link>

      {techList.length > 0 && (
        <ul className="projects-filter" role="tablist" aria-label={t('projects_filter_label')}>
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={!activeFilter}
              className={!activeFilter ? 'active' : ''}
              onClick={() => handleFilterChange(null)}>
              {t('projects_filter_all')}
            </button>
          </li>
          {techList.map(tech => (
            <li key={tech}>
              <button
                type="button"
                role="tab"
                aria-selected={activeFilter === tech}
                className={activeFilter === tech ? 'active' : ''}
                onClick={() => handleFilterChange(tech)}>
                {tech}
              </button>
            </li>
          ))}
        </ul>
      )}

      <ul className="projects-grid" ref={gridRef}>
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }) => (
                <StyledProject key={node.frontmatter.title}>{projectInner(node)}</StyledProject>
              ))}
          </>
        ) : (
          <TransitionGroup component={null} key={`filter-${activeFilter ?? 'all'}`}>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <CSSTransition
                  key={node.frontmatter.title}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}>
                  <StyledProject
                    ref={el => (revealProjects.current[i] = el)}
                    style={{
                      transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                    }}>
                    {projectInner(node)}
                  </StyledProject>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </ul>

      {hasMore && (
        <button
          type="button"
          className="more-button"
          aria-expanded={showMore}
          onClick={toggleShowMore}>
          {showMore ? t('projects_show_less') : t('projects_show_more')}
        </button>
      )}
    </StyledProjectsSection>
  );
};

export default Projects;
